import dayjs from 'dayjs';
import { User_Base } from '~/common/typescript/models/User';

export const ALLOWED_AVATAR_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
export const MAX_AVATAR_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

export interface AvatarValidationResult {
  valid: boolean;
  error?: string;
}

export interface PasswordStrengthResult {
  level: 'none' | 'weak' | 'medium' | 'strong';
  score: number;
  label: string;
  color: string;
}

export interface ProductSubscriptionInfo {
  key: string;
  name: string;
  tier: string;
  tierLabel: string;
  expireAt?: string;
  maxDevices?: number;
  isBlocked: boolean;
  isActive: boolean;
}

/**
 * Extracts up to 2 uppercase initials from fullname or username.
 */
export function getAvatarInitials(fullname?: string | null, username?: string | null): string {
  const raw = (fullname || username || '').trim();
  if (!raw) return 'U';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0][0] || '';
    const last = parts[parts.length - 1][0] || '';
    return `${first}${last}`.toUpperCase();
  }
  return raw.slice(0, 2).toUpperCase();
}

/**
 * Resolves avatar URL from Strapi media object or string.
 */
export function resolveAvatarUrl(avatar?: any, apiBaseUrl: string = ''): string | null {
  if (!avatar) return null;
  if (typeof avatar === 'string') {
    const trimmed = avatar.trim();
    if (!trimmed) return null;
    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('data:') ||
      trimmed.startsWith('blob:')
    ) {
      return trimmed;
    }
    const cleanBase = apiBaseUrl.replace(/\/$/, '');
    return cleanBase ? `${cleanBase}${trimmed.startsWith('/') ? '' : '/'}${trimmed}` : trimmed;
  }
  if (typeof avatar === 'object' && 'url' in avatar && typeof avatar.url === 'string') {
    return resolveAvatarUrl(avatar.url, apiBaseUrl);
  }
  return null;
}

/**
 * Validates Vietnamese phone number format (e.g., 0989340928 or +84989340928).
 * Empty string is considered valid when phone number is optional.
 */
export function validateVietnamesePhone(phone?: string | null): boolean {
  if (phone === undefined || phone === null) return true;
  const trimmed = String(phone).trim();
  if (trimmed === '') return true;
  return /^(0|\+84)[0-9]{8,10}$/.test(trimmed);
}

/**
 * Validates avatar file MIME type and size (<= 2MB).
 */
export function validateAvatarFile(file?: { type?: string; size?: number } | null): AvatarValidationResult {
  if (!file) {
    return { valid: false, error: 'Không tìm thấy tệp ảnh hợp lệ' };
  }
  const mime = (file.type || '').toLowerCase();
  if (!ALLOWED_AVATAR_MIME_TYPES.includes(mime)) {
    return {
      valid: false,
      error: 'Chỉ hỗ trợ ảnh định dạng JPG, PNG hoặc WebP',
    };
  }
  if (typeof file.size === 'number' && file.size > MAX_AVATAR_SIZE_BYTES) {
    return {
      valid: false,
      error: 'Dung lượng ảnh không được vượt quá 2MB',
    };
  }
  return { valid: true };
}

/**
 * Evaluates password strength for user feedback in the Change Password screen.
 */
export function evaluatePasswordStrength(password?: string | null): PasswordStrengthResult {
  const pwd = (password || '').trim();
  if (!pwd) {
    return { level: 'none', score: 0, label: '', color: '#d9d9d9' };
  }
  if (pwd.length < 6) {
    return { level: 'weak', score: 25, label: 'Yếu (tối thiểu 6 ký tự)', color: '#ff4d4f' };
  }

  let score = 35;
  if (pwd.length >= 8) score += 20;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 15;
  if (/[0-9]/.test(pwd)) score += 15;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 15;

  if (score >= 90) {
    return { level: 'strong', score: Math.min(100, score), label: 'Mạnh', color: '#52c41a' };
  }
  if (score >= 60) {
    return { level: 'medium', score, label: 'Trung bình', color: '#faad14' };
  }
  return { level: 'weak', score, label: 'Yếu', color: '#ff4d4f' };
}

/**
 * Builds FormData payload for ProfileAPIs.remote_updateProfile.
 */
export function buildProfileFormData(
  values: Record<string, any>,
  stagedAvatarFile?: File | null,
): FormData {
  const formData = new FormData();

  if (values.fullname !== undefined && values.fullname !== null && String(values.fullname).trim() !== '') {
    formData.append('fullname', String(values.fullname).trim());
  }
  if (values.pronouns !== undefined && values.pronouns !== null) {
    formData.append('pronouns', String(values.pronouns).trim());
  }
  if (values.job_title !== undefined && values.job_title !== null) {
    formData.append('job_title', String(values.job_title).trim());
  }
  if (values.department !== undefined && values.department !== null) {
    formData.append('department', String(values.department).trim());
  }
  if (values.aboutMe !== undefined && values.aboutMe !== null) {
    formData.append('aboutMe', String(values.aboutMe).trim());
  }
  if (values.phoneNumber !== undefined && values.phoneNumber !== null && String(values.phoneNumber).trim() !== '') {
    formData.append('phoneNumber', String(values.phoneNumber).trim());
  }

  if (values.birthday && dayjs.isDayjs(values.birthday)) {
    formData.append('birthday_year', values.birthday.year().toString());
    formData.append('birthday_month', (values.birthday.month() + 1).toString());
    formData.append('birthday_day', values.birthday.date().toString());
  }

  if (stagedAvatarFile) {
    formData.append('avatar', stagedAvatarFile);
  } else if (values.avatar && values.avatar.fileList && values.avatar.fileList[0]?.originFileObj) {
    formData.append('avatar', values.avatar.fileList[0].originFileObj);
  }

  return formData;
}

/**
 * Formats raw tier enum into natural Vietnamese label.
 */
export function formatSubscriptionTierLabel(tier?: string | null): string {
  const normalized = (tier || 'FREE').toUpperCase();
  if (normalized === 'FREE') return 'Miễn phí';
  return `Gói ${normalized}`;
}

/**
 * Extracts user's TLGeo product subscriptions from User_Base.
 */
export function getUserProductSubscriptions(user?: Partial<User_Base> | null): ProductSubscriptionInfo[] {
  const u = (user || {}) as Record<string, any>;

  const buildItem = (
    key: string,
    name: string,
    tierRaw: any,
    expireRaw: any,
    maxDevicesRaw: any,
    blockedRaw: any,
  ): ProductSubscriptionInfo => {
    const tier = tierRaw ? String(tierRaw).toUpperCase() : 'FREE';
    const isBlocked = Boolean(blockedRaw);
    const expireAt = expireRaw ? String(expireRaw) : undefined;
    const isExpired = expireAt ? dayjs(expireAt).isBefore(dayjs()) : false;
    const isActive = !isBlocked && (tier !== 'FREE' || Boolean(expireAt)) && !isExpired;
    return {
      key,
      name,
      tier,
      tierLabel: formatSubscriptionTierLabel(tier),
      expireAt,
      maxDevices: typeof maxDevicesRaw === 'number' ? maxDevicesRaw : undefined,
      isBlocked,
      isActive,
    };
  };

  return [
    buildItem('geocollect', 'GeoCollect', u.gcTier, u.gcExpireAt, u.gcMaxNDevices, u.gcBlocked),
    buildItem('geodesktop', 'GeoDesktop', u.gdTier, u.gdExpireAt, u.gdMaxNDevices, u.gdBlocked),
    buildItem('flightplanner', 'Flight Planner', u.fpTier, u.fpExpireAt, u.fpMaxNDevices, u.fpBlocked),
    buildItem('geocad', 'GeoCAD', u.geocadTier, u.geocadExpireAt, u.geocadMaxNDevices, u.geocadBlocked),
  ];
}
